import { AUTH_SOURCE } from "../../constants/authSource";
import { refreshCreAccessToken } from "../../cre/creTokenRefresh";
import { useGlobalStore } from "../../context/UserContext/userContext";
import { handleTokenExpired } from "../../utils/helper";

// Single in-flight refresh shared across all requests
let inFlight = null;

function ensureIframeListenerInitialized() {
  const store = useGlobalStore.getState();
  if (store._tokenBridgeInitialized) return;

  if (typeof window === "undefined") {
    store._tokenBridgeInitialized = true;
    return;
  }

  const onMessage = (event) => {
    const data = event?.data;
    if (!data || typeof data !== "object") return;
    if (data.type === "NEW_TOKEN" && typeof data.token === "string") {
      const token = data.token.trim();
      if (token) {
        useGlobalStore.setState({ accessToken: token });
      }
    }
  };

  window.addEventListener("message", onMessage);
  store._tokenBridgeInitialized = true;
}

function requestTokenFromParent(timeoutMs = 15000) {
  return new Promise((resolve, reject) => {
    if (typeof window === "undefined") {
      reject(new Error("No window for token refresh"));
      return;
    }

    const startedAt = Date.now();
    const onMessage = (event) => {
      const data = event?.data;
      if (!data || typeof data !== "object") return;
      if (data.type === "NEW_TOKEN" && typeof data.token === "string") {
        const token = data.token.trim();
        if (!token) return;
        window.removeEventListener("message", onMessage);
        useGlobalStore.setState({ accessToken: token });
        resolve(token);
      }
    };

    window.addEventListener("message", onMessage);
    try {
      window.parent?.postMessage({ type: "REQUEST_NEW_TOKEN" }, "*");
    } catch (e) {
      window.removeEventListener("message", onMessage);
      reject(e);
      return;
    }

    const tick = () => {
      if (Date.now() - startedAt > timeoutMs) {
        window.removeEventListener("message", onMessage);
        reject(new Error("Token refresh timeout"));
        return;
      }
      setTimeout(tick, 250);
    };
    tick();
  });
}

/**
 * Centralized refresh strategy:
 * - CRE: Entra silent refresh + re-validate
 * - Radical embed: parent iframe postMessage
 */
export async function refreshAccessToken() {
  if (inFlight) return inFlight;

  inFlight = (async () => {
    const store = useGlobalStore.getState();
    const { authSource } = store;

    try {
      if (authSource === AUTH_SOURCE.CRE) {
        return await refreshCreAccessToken();
      }

      ensureIframeListenerInitialized();
      // In embedded (non-MSAL) flow, retry parent token fetch a few times
      // before we consider the session expired.
      const MAX_ATTEMPTS = 3;
      let lastError = null;
      for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
        try {
          const token = await requestTokenFromParent();
          if (!token) throw new Error("No token from parent");
          return token;
        } catch (e) {
          lastError = e;
          // Small backoff: 250ms, 500ms, 750ms
          try {
            await new Promise((r) => setTimeout(r, 250 * attempt));
          } catch (_) {}
        }
      }

      throw lastError || new Error("Token refresh failed after retries");
    } catch (e) {
      // Final fallback
      handleTokenExpired();
      throw e;
    } finally {
      inFlight = null;
    }
  })();

  return inFlight;
}

